/**
 * Utility functions for smooth scrolling to page elements
 */

/**
 * Scrolls smoothly to an element with the specified ID
 * @param {string} elementId - The ID of the element to scroll to (without the # symbol)
 * @param {number} offset - Optional vertical offset in pixels (e.g., for fixed headers)
 * @param {number} delay - Optional delay in milliseconds before scrolling
 */
export const scrollToElement = (elementId, offset = 80, delay = 0) => {
  if (!elementId) return;
  
  // First try to find the element by ID
  let element = document.getElementById(elementId);
  
  // If not found, try to find by querySelector with id selector
  if (!element) {
    element = document.querySelector(`#${elementId}`);
  }
  
  // If still not found, try to find by querySelector with class selector
  if (!element) {
    element = document.querySelector(`.${elementId}`);
  }
  
  // If still not found, try to find by element with data-id attribute
  if (!element) {
    element = document.querySelector(`[data-id="${elementId}"]`);
  }
  
  if (!element) {
    console.error(`Element with ID "${elementId}" not found`);
    return;
  }
  
  if (delay > 0) {
    setTimeout(() => {
      scrollWithOffset(element, offset);
    }, delay);
  } else {
    scrollWithOffset(element, offset);
  }
};

/**
 * Scrolls to the element with the specified offset
 * @param {HTMLElement} element - The element to scroll to
 * @param {number} offset - Vertical offset in pixels
 */
const scrollWithOffset = (element, offset) => {
  const elementPosition = element.getBoundingClientRect().top;
  const offsetPosition = elementPosition + window.pageYOffset - offset;
  
  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth'
  });
};

/**
 * Handles click events for anchor links, implementing smooth scrolling
 * @param {Event} e - The click event
 * @param {string} href - The href attribute value
 * @param {number} offset - Optional vertical offset
 * @param {number} delay - Optional delay
 * @returns {boolean} - Whether the event was handled
 */
export const handleAnchorClick = (e, href, offset = 80, delay = 0) => {
  // Check if this is a hash link (anchor)
  if (href && href.startsWith('#')) {
    e.preventDefault(); // Prevent default anchor behavior
    
    // Get the target element ID
    const targetId = href.substring(1); // Remove the # symbol
    
    // Log that we're trying to scroll to the element
    console.log(`Attempting to scroll to element with ID: ${targetId}`);
    
    // Scroll to the element
    scrollToElement(targetId, offset, delay);
    return true;
  }
  
  return false;
};

/**
 * Scrolls to the element corresponding to the hash in the URL
 * Used for handling direct navigation to anchors
 * @param {number} offset - Optional vertical offset
 */
export const scrollToHashOnLoad = (offset = 80) => {
  if (window.location.hash) {
    const targetId = window.location.hash.substring(1);
    console.log(`Hash in URL detected: ${targetId}`);
    // Add a small delay to ensure the DOM is fully loaded
    setTimeout(() => {
      scrollToElement(targetId, offset);
    }, 100);
  }
}; 